/**
 * Writes `document.title` from the route match, so each history entry, tab and bookmark names its own
 * screen (APP-3602). One consumer, mounted at `AccountRoot`.
 */

import { useEffect, useMemo } from "react"
import { useSelector } from "react-redux"
import { get, last } from "lodash"
import { useMatches } from "react-router-dom"
import { getCurrentWatchlist } from "modules/watchlist-root/selectors"
import { getCurrentTopic } from "modules/research-topic-root/selectors"

const PRODUCT = "Portfolio Intelligence"
const EMPTY_PARAMS: RouteParams = {}

type RouteParams = Record<string, string | undefined>
type Matches = ReturnType<typeof useMatches>

interface Named { name?: string }
interface RouteHandle { title?: string, headerTitle?: string }

const getAccountName = (state: unknown): string | undefined => get(state, "global.selected.accountMeta.name")
// The alias that shipped WITH the name, in the same `selectAccount` payload — see the gate below.
const getLoadedAccountAlias = (state: unknown): string | undefined => get(state, "global.selected.accountMeta.alias")
// Gate on the loaded portfolio's id, not on `global.selected.portfolio`, which `handleNavigate` writes
// straight off the pathname — ahead of the data fetch. Same pattern as getCurrentWatchlist / getCurrentTopic.
const getLoadedPortfolioId = (state: unknown): string | undefined => get(state, "global.data.portfolio.id")
const getLoadedPortfolioName = (state: unknown): string | undefined => get(state, "global.data.portfolio.data.name")

// Screen before resource: the screen is what the user chose, and a resource-first title reads as a wall of
// repeated resource names when several screens of one portfolio are open.
export function composeTitle(screen?: string | null, resource?: string | null): string {
  const lead = [screen, resource].filter(Boolean).join(" · ")
  return lead ? `${lead} — ${PRODUCT}` : PRODUCT
}

// `headerTitle` already names the screen for the routes that render the plain app bar; a route only
// needs its own `title` when it has none, or when the bar's wording is too terse to stand alone.
export function resolveScreenTitle(matches: { handle?: unknown }[]): string | undefined {
  const titles = matches
    .map(match => {
      const handle = match.handle as RouteHandle | undefined
      return handle?.title || handle?.headerTitle
    })
    .filter((title): title is string => Boolean(title))
  return last(titles)
}

/**
 * The route decides which resource is in scope; the slot only decides whether it has resolved yet.
 * Reading it off the slot alone would name a portfolio on a watchlist page, because
 * `global.selected.portfolio` survives a navigation that does not match the portfolio path.
 */
function useCurrentResourceName(matches: Matches): string | null {
  const params: RouteParams = get(last(matches), "params", EMPTY_PARAMS)
  const loadedPortfolioId = useSelector(getLoadedPortfolioId)
  const loadedPortfolioName = useSelector(getLoadedPortfolioName)
  const watchlist = useSelector(state => getCurrentWatchlist(state, { params })) as Named | null
  const topic = useSelector(state => getCurrentTopic(state, { params })) as Named | null
  const accountName = useSelector(getAccountName)
  const loadedAccountAlias = useSelector(getLoadedAccountAlias)

  if (params.portfolioId) {
    const expectedId = `/api/${params.accountValue}/p/${params.portfolioId}`
    return loadedPortfolioId === expectedId ? loadedPortfolioName || null : null
  }
  if (params.watchlistId) { return watchlist?.name || null }
  if (params.topicId) { return topic?.name || null }
  // Same gate as the resource branches, compared against `accountMeta.alias` rather than
  // `global.selected.account`: the NAVIGATE reducer writes `selected.account` straight off a `/p/`
  // pathname without touching `accountMeta`, so it runs ahead of the meta fetch and cannot prove the
  // name matches the route. The alias ships in the same payload as the name, so they cannot disagree.
  if (params.accountValue && params.accountValue !== loadedAccountAlias) { return null }
  return accountName || null
}

export default function useDocumentTitle(): void {
  const matches = useMatches()
  const screen = useMemo(() => resolveScreenTitle(matches), [matches])
  const resource = useCurrentResourceName(matches)

  useEffect(() => {
    document.title = composeTitle(screen, resource)
  }, [screen, resource])
}
